import { AxiosPromise, AxiosResponse } from 'axios';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';

import { api } from 'api';
import { OrderStatistics } from 'features/dashboard/ts/OrderStatistics';
import { LinearChartItem } from 'features/dashboard/ts/LinearCharItem';
import { LinearGraphQuery } from 'features/dashboard/ts/LinearGraphQuery';
import { RadialChartItem } from 'features/dashboard/ts/RadialChartItem';
import { OrderOutputStatus } from 'features/dashboard/ts/OrderOutputStatus';
import { InputOrder } from 'features/dashboard/ts/InputOrder';
import { DefaultSuccessResponse } from 'ts/DefaultSuccessResponse';
import { getFileRequest, GetFileRequestResponse } from 'features/app/api/getFileRequest';
import { SendBulkOrder } from 'features/dashboard/ts/SendBulkOrder';
import { BulkEmailVerificationType, OrderSummary } from 'features/dashboard/ts/OrderSummary';
import i18n from 'locales/i18n';

export const getOrderStatistics = (): AxiosPromise<OrderStatistics> => api.get(`/orders/statistics/`);

export const getLinearGraph = (query: LinearGraphQuery, apiUrl: string): AxiosPromise<LinearChartItem[]> =>
  from(
    api.get(apiUrl, {
      params: {
        ...query,
        created_at_before: query.created_at_before.toISOString(),
        created_at_after: query.created_at_after.toISOString(),
      },
    })
  )
    .pipe(
      map((response: AxiosResponse<LinearChartItem[]>) => ({
        ...response,
        data: response.data.map(item => ({ ...item, date: new Date(item.date) })),
      }))
    )
    .toPromise();

export const getOrderSummary = (query: LinearGraphQuery): AxiosPromise<RadialChartItem[]> =>
  from(api.get('/orders/orders-status-graph/', { params: query }))
    .pipe(
      map((response: AxiosResponse<OrderOutputStatus>) => {
        const data = [
          {
            name: i18n.t('onDelivery', { ns: 'dashboard' }),
            total: response.data.processing,
          },
          {
            name: i18n.t('delivered', { ns: 'dashboard' }),
            total: response.data.delivered,
          },
          {
            name: i18n.t('canceled', { ns: 'dashboard' }),
            total: response.data.failed,
          },
        ];

        return { ...response, data };
      })
    )
    .toPromise();

export const createOrder = (data: InputOrder): AxiosPromise<DefaultSuccessResponse> =>
  api.post('/orders/create-order/', data);

export const getSingleOrderSummary = (data: InputOrder): AxiosPromise<OrderSummary> =>
  api.post('/orders/order-summary/', data);

export const bulkEmailsVerification = (formData: FormData): AxiosPromise<Array<BulkEmailVerificationType>> =>
  api.post('/orders/bulk-email-verification/', formData);

export const createBulkOrder = (data: InputOrder[]): AxiosPromise<DefaultSuccessResponse> =>
  api.post('/orders/send-bulk-order/', data);

export const getBulkOrderTemplate = async (): GetFileRequestResponse => getFileRequest('/orders/bulk-order-template/');

export const uploadBulkOrder = (formData: FormData): AxiosPromise<SendBulkOrder> =>
  api.post('/orders/bulk-order-template/upload/', formData);
